import * as React from 'react';
import moment from 'moment';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import { alpha } from '@mui/material/styles';

import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import LanguageRoundedIcon from '@mui/icons-material/LanguageRounded';
import AlternateEmailRoundedIcon from '@mui/icons-material/AlternateEmailRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import MapRoundedIcon from '@mui/icons-material/MapRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { StarRating } from '../Ratings';
import { ReviewList } from '../ReviewList';
import { MapView } from '../MapView';

import {
  groupedAmenities,
  knownAmenities,
  amenityLabel,
  mediaAlbums,
  mediaInAlbum,
  listingMedia,
  statedPolicies,
  statedOpeningHours,
  googleMapsUrlFor,
  normalizeSeasonalRates,
  seasonCovering,
} from '../../../data/listingAttributes';
import { getLocation } from '../../../data/locations';
import { formatUnitPrice } from '../../../utils/pricing';
import { getListingUpdates, hasUnseenUpdates, markUpdatesSeen } from '../../../utils/listingUpdates';

/**
 * The panels behind the listing page's section tabs.
 *
 * Split out of index.js, which owns the masthead, the photo mosaic and which
 * tab is showing. Each panel here renders one section and nothing else, and
 * every one of them is driven by provider data — the amenity list this page
 * used to show was hardcoded and identical on every listing, down to a
 * national park advertising a bar.
 *
 * Which tabs exist at all is decided in index.js by `visibleSections`, so a
 * panel here never has to render an "unavailable" state: if it is on screen,
 * it has something to say.
 */

const SectionHeading = ({ children, hint }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="h6" component="h2" sx={{ fontWeight: 800 }}>
      {children}
    </Typography>
    {hint && (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: '62ch' }}>
        {hint}
      </Typography>
    )}
  </Box>
);

const Tick = () => (
  <CheckRoundedIcon sx={{ fontSize: '1.05rem', color: 'primary.main', flexShrink: 0 }} />
);

const Note = ({ children }) => (
  <Box
    sx={{
      display: 'flex',
      gap: 1.25,
      p: 2,
      mt: 2,
      borderRadius: 2,
      border: 1,
      borderColor: 'divider',
      color: 'text.secondary',
    }}
  >
    <InfoOutlinedIcon sx={{ fontSize: '1.1rem', color: 'primary.main', flexShrink: 0, mt: 0.2 }} />
    <Typography variant="body2">{children}</Typography>
  </Box>
);

/* ────────────────────────────────────────────────────────────
 * Updates
 * ──────────────────────────────────────────────────────────── */

/**
 * The operator's posts, and whether this device has seen them.
 *
 * Loaded here rather than in the panel because the TAB needs to know: a dot
 * that only appears once you open the tab it is meant to draw you to would be
 * pointless.
 *
 * `unseen` is latched at load. Opening the tab marks them read, and the dot
 * clears — but it must not clear while the request is still in flight, which
 * is why this is state rather than recomputed on every render.
 */
export function useListingUpdates(listingId) {
  const [updates, setUpdates] = React.useState([]);
  const [unseen, setUnseen] = React.useState(false);

  React.useEffect(() => {
    let live = true;
    if (!listingId) {
      setUpdates([]);
      setUnseen(false);
      return undefined;
    }

    getListingUpdates(listingId).then((next) => {
      if (!live) return;
      setUpdates(next);
      setUnseen(hasUnseenUpdates(next, listingId));
    });

    return () => { live = false; };
  }, [listingId]);

  const markSeen = React.useCallback(() => {
    markUpdatesSeen(listingId);
    setUnseen(false);
  }, [listingId]);

  return { updates, unseen, markSeen };
}

export function UpdatesPanel({ listing, updates }) {
  return (
    <Box>
      <SectionHeading hint={`News from ${listing?.company?.name || 'the operator'} — closures, changes, what is new.`}>
        Updates
      </SectionHeading>

      <Box sx={{ maxWidth: 720 }}>
        {updates.map((update, i) => (
          <Box
            key={update.id}
            sx={{
              display: 'grid',
              gap: { xs: 0.5, sm: 2 },
              gridTemplateColumns: { xs: '1fr', sm: '110px 1fr' },
              py: 2,
              borderTop: i === 0 ? 0 : 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {update.createdAtMillis ? moment(update.createdAtMillis).format('D MMM YYYY') : ''}
            </Typography>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.25 }}>
                {update.title}
              </Typography>
              {update.body && (
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                  {update.body}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/* ────────────────────────────────────────────────────────────
 * Overview
 * ──────────────────────────────────────────────────────────── */

const DESCRIPTION_TRUNCATE_AT = 280;

export function OverviewPanel({ listing }) {
  const [expanded, setExpanded] = React.useState(false);

  const description = listing?.description || '';
  const isLong = description.length > DESCRIPTION_TRUNCATE_AT;
  const shown = isLong && !expanded
    ? `${description.slice(0, DESCRIPTION_TRUNCATE_AT).trimEnd()}…`
    : description;

  const tags = Array.isArray(listing?.tags) ? listing.tags : [];
  // The first handful, as the design has it. Facilities shows all of them
  // grouped — one field, rendered twice, so the two can never disagree.
  const offers = knownAmenities(listing?.amenities).slice(0, 9);
  const contact = listing?.contact || {};
  const hours = statedOpeningHours(listing?.openingHours);
  const mapsUrl = googleMapsUrlFor(listing?.googlePlaceId);

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {listing?.type && (
          <Chip
            label={listing.type}
            size="small"
            variant="outlined"
            sx={{ borderColor: 'primary.main', color: 'primary.main' }}
          />
        )}
        {tags.map((tag) => (
          <Chip key={tag} label={tag} size="small" variant="outlined" />
        ))}
      </Box>

      <SectionHeading>About</SectionHeading>
      <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: 'pre-line', maxWidth: '65ch' }}>
        {shown}
      </Typography>
      {isLong && (
        <Button variant="text" size="small" onClick={() => setExpanded((v) => !v)} sx={{ mt: 0.5, px: 0 }}>
          {expanded ? 'Read less' : 'Read more'}
        </Button>
      )}

      {(contact.phone || contact.email || contact.website || contact.instagram || listing?.address) && (
        <Box
          sx={{
            mt: 3,
            p: 2.5,
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            maxWidth: 460,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5 }}>
            Contact
          </Typography>
          {contact.phone && (
            <ContactRow icon={<PhoneRoundedIcon fontSize="small" />} href={`tel:${contact.phone}`}>
              {contact.phone}
            </ContactRow>
          )}
          {contact.email && (
            <ContactRow icon={<MailOutlineRoundedIcon fontSize="small" />} href={`mailto:${contact.email}`}>
              {contact.email}
            </ContactRow>
          )}
          {contact.website && (
            <ContactRow
              icon={<LanguageRoundedIcon fontSize="small" />}
              href={/^https?:\/\//i.test(contact.website) ? contact.website : `https://${contact.website}`}
              external
            >
              {contact.website.replace(/^https?:\/\//i, '')}
            </ContactRow>
          )}
          {contact.instagram && (
            <ContactRow icon={<AlternateEmailRoundedIcon fontSize="small" />}>
              {contact.instagram}
            </ContactRow>
          )}
          {listing?.address && (
            <ContactRow icon={<LocationOnRoundedIcon fontSize="small" />}>{listing.address}</ContactRow>
          )}
          {mapsUrl && (
            /* A LINK, not an API call. The place id is the one Places field
               Google allows to be stored, and a Maps URL built from it costs
               nothing and carries no attribution obligation — unlike the
               ratings and hours the reference design pulled from Places. */
            <ContactRow icon={<MapRoundedIcon fontSize="small" />} href={mapsUrl} external>
              View on Google Maps
            </ContactRow>
          )}
        </Box>
      )}

      {hours.length > 0 && (
        <Box
          sx={{ mt: 3, p: 2.5, borderRadius: 2, border: 1, borderColor: 'divider', maxWidth: 460 }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
            Opening hours
          </Typography>
          {/* Stated by the operator. Days they have not spoken for are absent
              rather than shown as closed — see data/listingAttributes.js. */}
          {hours.map((day) => (
            <Box
              key={day.key}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 2,
                py: 0.75,
                borderBottom: 1,
                borderColor: 'divider',
                '&:last-of-type': { borderBottom: 0 },
              }}
            >
              <Typography variant="body2">{day.label}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {day.hours}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {offers.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <SectionHeading>What this place offers</SectionHeading>
          <Box
            sx={{
              display: 'grid',
              gap: 1,
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            }}
          >
            {offers.map((value) => (
              <Box key={value} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tick />
                <Typography variant="body2" color="text.secondary">
                  {amenityLabel(value)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

function ContactRow({ icon, href, external, children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, py: 0.75 }}>
      <Box sx={{ color: 'primary.main', display: 'flex', mt: 0.2 }}>{icon}</Box>
      {href ? (
        <Typography
          component="a"
          href={href}
          {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
          variant="body2"
          sx={{ color: 'text.primary', textDecoration: 'none', borderBottom: 1, borderColor: 'divider' }}
        >
          {children}
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {children}
        </Typography>
      )}
    </Box>
  );
}

/* ────────────────────────────────────────────────────────────
 * Gallery
 * ──────────────────────────────────────────────────────────── */

export function GalleryPanel({ listing }) {
  const albums = mediaAlbums(listing);
  const [album, setAlbum] = React.useState('all');
  const photos = mediaInAlbum(listing, album);

  return (
    <Box>
      {/* Only worth showing when there is a choice to make: a listing whose
          photos are all uncategorised has one album, and a filter with a
          single option is furniture. */}
      {albums.length > 1 && (
        <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1.5, mb: 1 }}>
          {albums.map((entry) => (
            <Chip
              key={entry.value}
              label={`${entry.label} · ${entry.count}`}
              onClick={() => setAlbum(entry.value)}
              variant={album === entry.value ? 'filled' : 'outlined'}
              color={album === entry.value ? 'primary' : 'default'}
              sx={{ flexShrink: 0 }}
            />
          ))}
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' },
        }}
      >
        {photos.map((photo, i) => (
          <Box
            key={`${photo.url}-${i}`}
            component="img"
            src={photo.url}
            alt={photo.caption || `${listing?.name || 'Listing'} photo ${i + 1}`}
            loading="lazy"
            sx={{
              width: '100%',
              aspectRatio: '4 / 3',
              objectFit: 'cover',
              borderRadius: 2,
              display: 'block',
              backgroundColor: 'action.hover',
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

/* ────────────────────────────────────────────────────────────
 * Facilities
 * ──────────────────────────────────────────────────────────── */

export function FacilitiesPanel({ listing }) {
  const groups = groupedAmenities(listing?.amenities);

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
      }}
    >
      {groups.map(({ group, amenities }) => (
        <Box key={group} sx={{ p: 2.5, borderRadius: 2, border: 1, borderColor: 'divider' }}>
          <Typography
            variant="overline"
            sx={{ fontWeight: 800, letterSpacing: '0.12em', color: 'text.secondary' }}
          >
            {group}
          </Typography>
          <Box sx={{ display: 'grid', gap: 1, mt: 1 }}>
            {amenities.map((amenity) => (
              <Box key={amenity.value} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tick />
                <Typography variant="body2" color="text.secondary">
                  {amenity.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

/* ────────────────────────────────────────────────────────────
 * Pricing
 * ──────────────────────────────────────────────────────────── */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `06-01` → `1 Jun`. Never a year: these recur. */
function formatSeasonDate(mmdd) {
  const [month, day] = String(mmdd).split('-').map(Number);
  return `${day} ${MONTHS[month - 1]}`;
}

/**
 * The published rate card.
 *
 * ⚠ THESE ARE NOT WHAT A BOOKING COSTS. A booking is priced from `cost` and
 * `priceUnit` through utils/pricing.js — the module mirrored with the provider
 * app and the functions repo. So this panel shows the seasons the operator
 * published AND, at the bottom, the price a booking made here is actually
 * charged. Quoting $580 in a card and charging $450 at checkout is the one
 * outcome this section must not produce.
 */
export function PricingPanel({ listing, onReserve, booked }) {
  const rates = normalizeSeasonalRates(listing?.seasonalRates);
  const current = seasonCovering(rates, new Date());

  return (
    <Box>
      <SectionHeading hint="Rates published by the operator. What you pay when you book through Vhakacha is shown below.">
        Seasonal rates
      </SectionHeading>

      <Box sx={{ display: 'grid', gap: 1.5 }}>
        {rates.map((rate) => {
          const isCurrent = current?.id === rate.id;
          return (
            <Box
              key={rate.id}
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', sm: '1fr auto' },
                p: 2.5,
                borderRadius: 2,
                border: 1,
                borderColor: isCurrent ? 'primary.main' : 'divider',
                backgroundColor: (theme) =>
                  isCurrent ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
              }}
            >
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    {rate.label}
                  </Typography>
                  {/* Said plainly rather than left for the traveller to work
                      out from four date ranges. */}
                  {isCurrent && <Chip size="small" color="primary" label="Now" />}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mt: 0.5, mb: 1.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                    {formatSeasonDate(rate.startDate)} – {formatSeasonDate(rate.endDate)}
                  </Typography>
                  {rate.minNights && (
                    <Typography variant="body2" color="text.secondary">
                      Minimum {rate.minNights} {rate.minNights === 1 ? 'night' : 'nights'}
                    </Typography>
                  )}
                </Box>

                {rate.inclusions.length > 0 && (
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 0.75,
                      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                    }}
                  >
                    {rate.inclusions.map((inclusion) => (
                      <Box key={inclusion} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tick />
                        <Typography variant="body2" color="text.secondary">
                          {inclusion}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              <Box sx={{ textAlign: { xs: 'left', sm: 'right' }, alignSelf: 'center' }}>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 800, color: 'primary.main', fontVariantNumeric: 'tabular-nums' }}
                >
                  {formatUnitPrice(rate.price, rate.currency, rate.priceUnit)}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      {listing?.rateNote && <Note>{listing.rateNote}</Note>}

      <Box
        sx={{
          mt: 2.5,
          p: 2.5,
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="body2" color="text.secondary">
            Booking through Vhakacha
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
            {formatUnitPrice(listing?.cost, listing?.currency, listing?.priceUnit)}
          </Typography>
        </Box>
        <Button variant="contained" onClick={onReserve} disabled={booked}>
          {booked ? 'Booked' : 'Check availability'}
        </Button>
      </Box>
    </Box>
  );
}

/* ────────────────────────────────────────────────────────────
 * Policies
 * ──────────────────────────────────────────────────────────── */

export function PoliciesPanel({ listing }) {
  const policies = statedPolicies(listing?.policies);

  return (
    <Box sx={{ maxWidth: 720, borderRadius: 2, border: 1, borderColor: 'divider', px: 2.5 }}>
      {policies.map((policy, i) => (
        <Box key={policy.key} sx={{ py: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
            {policy.label}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
            {policy.value}
          </Typography>
          {i < policies.length - 1 && <Divider sx={{ mt: 2 }} />}
        </Box>
      ))}
    </Box>
  );
}

/* ────────────────────────────────────────────────────────────
 * Reviews
 * ──────────────────────────────────────────────────────────── */

export function ReviewsPanel({ listing }) {
  return (
    <Box>
      {/* The aggregate is repeated here on purpose: the stars in the masthead
          are out of sight once someone is reading the reviews, and "4.3 from
          12" is the context each individual review is read against. */}
      <Box sx={{ mb: 1.5 }}>
        <StarRating listing={listing} />
      </Box>
      <ReviewList listingId={listing?.id} />
    </Box>
  );
}

/* ────────────────────────────────────────────────────────────
 * Location
 * ──────────────────────────────────────────────────────────── */

export function LocationPanel({ listing }) {
  const region = getLocation(listing?.location)?.region;

  return (
    <Box>
      <SectionHeading>Where you'll be</SectionHeading>

      <Box sx={{ position: 'relative', height: { xs: '40vh', md: 380 }, borderRadius: 2, overflow: 'hidden' }}>
        <MapView providers={[listing]} ipInfo={listing} />
        {region && (
          <Chip
            label={region}
            size="small"
            sx={{
              position: 'absolute',
              top: 12,
              left: 12,
              fontWeight: 700,
              backgroundColor: 'background.paper',
              border: 1,
              borderColor: 'divider',
            }}
          />
        )}
      </Box>

      {(listing?.address || listing?.location) && (
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2 }}>
          {[listing.address, listing.location].filter(Boolean).join(', ')}
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary">
        Exact directions are sent with your booking confirmation. This map is an approximation.
      </Typography>
    </Box>
  );
}

/**
 * Which sections have something to say, in tab order.
 *
 * A tab is offered only when its panel has content. The reference design shows
 * "No facilities information currently available" and an empty Reviews panel;
 * four empty tabs out of seven reads as a broken page, where a missing tab
 * reads as a listing that has not filled that part in. Overview and Reviews
 * are always present — one is the listing itself, the other has an empty state
 * that says something true ("No reviews yet") rather than nothing.
 */
export function visibleSections(listing, { hasUpdates = false } = {}) {
  // Updates lead, as the design has it: a closure notice is worth more to
  // someone arriving on Friday than the description they have already read.
  // Passed in rather than derived from the listing, because posts live in a
  // subcollection and are fetched separately.
  const sections = hasUpdates ? [{ key: 'updates', label: 'Updates' }] : [];

  sections.push({ key: 'overview', label: 'Overview' });

  if (listingMedia(listing).length) sections.push({ key: 'gallery', label: 'Gallery' });
  if (groupedAmenities(listing?.amenities).length) sections.push({ key: 'facilities', label: 'Facilities' });
  if (normalizeSeasonalRates(listing?.seasonalRates).length) sections.push({ key: 'pricing', label: 'Pricing' });
  if (statedPolicies(listing?.policies).length) sections.push({ key: 'policies', label: 'Policies' });

  sections.push({ key: 'reviews', label: 'Reviews' });

  if (listing?.coordinates) sections.push({ key: 'location', label: 'Location' });

  return sections;
}
